package ui.server.detail


import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.CurrencyType
import api.ResourceType
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.*
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.server.portal.PortalIO
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.EFieldRegularLong
import tools.EFieldTextString
import tools.GlobalObjectShare
import tools.rememberString
import ui.resource.ResourceListingArgs
import ui.widget.*
import kotlin.math.roundToInt

data class ServerArgs(var serverId: Long = -1)

@ComposableNavNode(ServerArgs::class)
@Composable
fun ServerUI() {
    with(ServerScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun ServerScope.Content(nnvl: ServerScope.Wrapper) {

    val deleteState = DialogState.remember(key = "delete")
    val lockState = DialogState.remember(key = "lock")

    DeleteDialog(deleteState, nnvl.data)
    LockDialog(lockState, nnvl)

    val isLockVisible = remember {
        mutableStateOf(!nnvl.change.value)
    }

    LaunchedEffect(nnvl.change.value) {
        isLockVisible.value = !nnvl.change.value
    }

    val toolbarActions = remember(nnvl.editable) {
        buildList {
            if (nnvl.canDelete) {
                add(ToolbarAction.delete { deleteState.show() })
            }

            if (nnvl.editable) {
                add(ToolbarAction(Svg.Lock_question, isLockVisible) {
                    lockState.show()
                })
                add(ToolbarAction.save(nnvl.change) { action(ServerVM.Action.Save) })
            }
        }
    }

    Column(Modifier.fillMaxSize()) {
        val title = Res.string.server_detail__title.rememberString()
        val toolbarTitle = remember(title, nnvl.data.id) {
            title to "(${nnvl.data.id})"
        }
        Toolbar(
            nav = nav,
            titlePrefix = toolbarTitle.first,
            titleSuffix = toolbarTitle.second,
            actions = toolbarActions
        )
        val cells = when {
            display.isMobile && display.isLand() -> StaggeredGridCells.Fixed(2)
            display.isMobile -> StaggeredGridCells.Fixed(1)
            else -> StaggeredGridCells.Adaptive(384.dp)
        }
        LazyVerticalStaggeredGrid(
            columns = cells,
            modifier = Modifier.fillMaxSize(),
            verticalItemSpacing = 8.dp,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(8.dp)
        ) {
            item {
                InfoPrimary(nnvl)
            }
            item {
                InfoCompany(nnvl)
            }
            item {
                ResponsibleInfo(nnvl)
            }
            item {
                PriceInfo(nnvl)
            }
            item {
                DomainInfo(nnvl)
            }
            item {
                LogoInfo(nnvl)
            }
        }
    }
}


@Composable
private fun ServerScope.DomainInfo(nnvl: ServerScope.Wrapper) {
    SimpleCard(Res.string.server_detail__domain_title) {
        FieldText(
            nnvl = nnvl,
            icon = Svg.Format_text,
            label = Res.string.server_detail__domain_label_title,
            field = nnvl.domainTitle
        )

        FieldText(
            nnvl = nnvl,
            icon = Svg.Web,
            label = Res.string.server_detail__domain_label_url_home,
            field = nnvl.urlHome
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Web,
            label = Res.string.server_detail__domain_label_url_panel,
            field = nnvl.urlControl
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Web,
            label = Res.string.server_detail__domain_label_url_game,
            field = nnvl.urlGame
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Web,
            label = Res.string.server_detail__domain_label_url_docs,
            field = nnvl.urlDocs
        )
    }
}

@Composable
private fun ServerScope.LogoInfo(nnvl: ServerScope.Wrapper) {
    SimpleCard(Res.string.server_detail__logo_title) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            FieldImage(
                nnvl = nnvl,
                label = Res.string.server_detail__domain_label_ico,
                field = nnvl.imageIco,
                defaultRes = PortalIO.ImplServer.DEFAULT_IMAGE_ICO,
                modifier = Modifier.weight(1F)
            )
            FieldImage(
                nnvl = nnvl,
                label = Res.string.server_detail__domain_label_logo_login,
                field = nnvl.imageLogoSquare,
                defaultRes = PortalIO.ImplServer.DEFAULT_IMAGE_SQUARE,
                modifier = Modifier.weight(1F)
            )
            FieldImage(
                nnvl = nnvl,
                label = Res.string.server_detail__domain_label_logo_panel,
                field = nnvl.imageLogoHorizontal,
                defaultRes = PortalIO.ImplServer.DEFAULT_IMAGE_HORIZONTAL,
                modifier = Modifier.weight(1F)
            )
        }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            FieldImage(
                nnvl = nnvl,
                label = Res.string.server_detail__logo_label_docs_light,
                field = nnvl.imageLogoDocsLight,
                defaultRes = PortalIO.ImplServer.DEFAULT_IMAGE_HORIZONTAL,
                modifier = Modifier.weight(1F)
            )
            FieldImage(
                nnvl = nnvl,
                label = Res.string.server_detail__logo_label_docs_dark,
                field = nnvl.imageLogoDocsDark,
                defaultRes = PortalIO.ImplServer.DEFAULT_IMAGE_HORIZONTAL,
                modifier = Modifier.weight(1F)
            )
        }
    }
}

@Composable
private fun ServerScope.InfoPrimary(nnvl: ServerScope.Wrapper) {
    SimpleCard(Res.string.server_detail__primary_label) {
        FieldText(
            nnvl = nnvl,
            icon = Svg.Format_text,
            label = Res.string.server_detail__primary_label_name,
            field = nnvl.name
        )
        if (nnvl.canSwap) {
            Button(
                enabled = !vm.isServerSwitching,
                onClick = { action(ServerVM.Action.Swap) },
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(Res.string.server_detail__action_activate.rememberString())
            }
        }
    }
}

@Composable
private fun ServerScope.InfoCompany(nnvl: ServerScope.Wrapper) {
    SimpleCard(Res.string.server_detail__company_label) {
        FieldText(
            nnvl = nnvl,
            icon = Svg.Store,
            label = Res.string.server_detail__company_label_name,
            field = nnvl.companyName
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Marker,
            label = Res.string.server_detail__company_label_addr,
            field = nnvl.companyAddr
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Key,
            label = Res.string.server_detail__company_label_vat,
            field = nnvl.companyVat
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Key,
            label = Res.string.server_detail__company_label_id,
            field = nnvl.companyId
        )
    }
}

@Composable
private fun ServerScope.ResponsibleInfo(nnvl: ServerScope.Wrapper) {
    SimpleCard(Res.string.server_detail__responsible_label) {
        FieldText(
            nnvl = nnvl,
            icon = Svg.Account,
            label = Res.string.server_detail__responsible_label_name,
            field = nnvl.responsibleName
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Email,
            label = Res.string.server_detail__responsible_label_email,
            field = nnvl.responsibleEmail
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Phone,
            label = Res.string.server_detail__responsible_label_tel,
            field = nnvl.responsibleTel
        )
        FieldText(
            nnvl = nnvl,
            icon = Svg.Web,
            label = Res.string.server_detail__responsible_label_www,
            field = nnvl.responsibleWww
        )
    }
}

@Composable
private fun ServerScope.PriceInfo(nnvl: ServerScope.Wrapper) {
    var isCurrencyExpanded by remember {
        mutableStateOf(false)
    }
    val animation by animateFloatAsState(
        animationSpec = tween(200),
        targetValue = if (isCurrencyExpanded) 180F
        else 0F
    )
    SimpleCard(Res.string.server_detail__price_label) {

        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.padding(horizontal = 8.dp)
                .padding(top = 8.dp)
        ) {
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                Icon(
                    imageVector = Svg.Sack_percent,
                    contentDescription = null,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Text(
                    text = Res.string.server_detail__price_label_pct.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
                Text(
                    text = "${(nnvl.pricePct.state * 100F).roundToInt()}%",
                    textAlign = TextAlign.End,
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.align(Alignment.CenterVertically)
                        .fillMaxWidth()
                )
            }
            Slider(
                steps = 100,
                value = nnvl.pricePct.state,
                enabled = nnvl.editable,
                colors = SliderDefaults.colors(
                    inactiveTickColor = Color.Transparent,
                    activeTickColor = Color.Transparent,
                ),
                onValueChange = { nnvl.pricePct.set(it) },
                modifier = Modifier.fillMaxWidth()
            )
        }

        HorizontalDivider()

        Card(
            onClick = { isCurrencyExpanded = true },
            enabled = nnvl.editable
        ) {
            Row(Modifier.padding(8.dp)) {
                Column(
                    modifier = Modifier.weight(1F),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        Icon(
                            imageVector = Svg.Currency_sign,
                            contentDescription = null,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                        Text(text = Res.string.server_detail__price_label_currency.rememberString())

                    }
                    Text(
                        text = CurrencyType[nnvl.priceCurrencyType.state].key,
                        color = if (CurrencyType.UNDEFINED match nnvl.priceCurrencyType.state) MaterialTheme.colorScheme.error
                        else Color.Unspecified
                    )
                }
                Icon(
                    imageVector = Svg.Chevron_down,
                    contentDescription = null,
                    modifier = Modifier
                        .align(Alignment.CenterVertically)
                        .rotate(animation)
                )
            }
            DropdownMenu(
                expanded = isCurrencyExpanded,
                onDismissRequest = { isCurrencyExpanded = false }) {
                CurrencyType.all.forEach {
                    DropdownMenuItem(
                        text = { Text(it.key) },
                        onClick = {
                            nnvl.priceCurrencyType.set(it.mask)
                            isCurrencyExpanded = false
                        })
                }
            }
        }

        HorizontalDivider()

        FieldText(
            nnvl = nnvl,
            icon = Svg.Account,
            label = Res.string.server_detail__price_label_person_name,
            field = nnvl.pricePersonName
        )

        FieldText(
            nnvl = nnvl,
            icon = Svg.Email,
            label = Res.string.server_detail__price_label_person_mail,
            field = nnvl.pricePersonMail
        )

    }
}


@Composable
private fun SimpleCard(title: StringResource, body: @Composable ColumnScope.() -> Unit) {
    Column(
        Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = title.rememberString(),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(start = 4.dp)
        )
        Card(Modifier.fillMaxWidth()) {
            Column(
                verticalArrangement = Arrangement.spacedBy(16.dp),
                modifier = Modifier.padding(8.dp),
                content = body
            )
        }
    }
}

@Composable
private fun FieldText(
    nnvl: ServerScope.Wrapper,
    icon: ImageVector,
    label: StringResource,
    field: EFieldTextString
) {
    TextField(
        shape = RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp),
        value = field.text,
        enabled = nnvl.editable,
        label = { Text(label.rememberString()) },
        leadingIcon = { Icon(icon, null) },
        isError = field.err,
        modifier = Modifier.fillMaxWidth(),
        onValueChange = { field.set(it) })
}


@Composable
fun ServerScope.FieldImage(
    nnvl: ServerScope.Wrapper,
    label: StringResource,
    field: EFieldRegularLong,
    defaultRes: Long,
    modifier: Modifier
) {
    fun openResourcePicker() {
        GlobalObjectShare.launchImagePicker(
            key = field.fieldName,
            nav = nav,
            args = ResourceListingArgs(
                companyId = login.company.id,
                restrictionType = ResourceType.IMAGE.mask,
            )
        ) {
            it.firstOrNull()?.apply {
                field.set(id)
            }
        }
    }

    GlobalObjectShare.Consumer(field.fieldName)

    Column(modifier.clickable(nnvl.editable) { openResourcePicker() }) {
        Box(
            Modifier.fillMaxWidth()
                .height(128.dp)

        ) {
            LazyImage(
                id = field.state,
                contentScale = ContentScale.Fit,
                alphaBackground = true,
                modifier = Modifier.fillMaxSize()
            )

            if (defaultRes != field.state) {
                Icon(
                    imageVector = Svg.DeleteEmpty,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onErrorContainer,
                    modifier = Modifier.align(Alignment.TopEnd)
                        .padding(4.dp)
                        .background(MaterialTheme.colorScheme.errorContainer, CircleShape)
                        .clip(CircleShape)
                        .clickable { field.set(defaultRes) }
                        .padding(8.dp)
                        .size(18.dp)
                )
            }
        }
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier
                .fillMaxWidth()
                .background(Color.Black)
                .padding(8.dp)
        ) {

            Text(
                text = label.rememberString(),
                color = Color.White,
                style = MaterialTheme.typography.labelMedium,
                modifier = Modifier.weight(1F)
                    .align(Alignment.CenterVertically)
            )
            if (nnvl.editable) {
                Icon(
                    imageVector = Svg.Edit,
                    contentDescription = null,
                    tint = Color.White,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
    }
}

@Composable
fun ServerScope.DeleteDialog(deleteState: DialogState, data: Io.Server) {
    val areDependencyDeleted = remember {
        data.countCompany == 1
            && data.countLocation == 1
            && data.countUser == 0
            && data.countDevice == 0
    }
    val confirmButton: @Composable () -> Unit = {
        if (areDependencyDeleted) {
            TextButton({
                action(ServerVM.Action.Delete)
                deleteState.hide()
            }) { Text(Res.string.dialog__action_delete.rememberString()) }
        }
    }
    Dialog(deleteState) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { deleteState.hide() },
            title = { Text(Res.string.server_detail__delete_title.rememberString()) },
            confirmButton = confirmButton,
            dismissButton = { TextButton({ deleteState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            if (areDependencyDeleted) {
                Text(Res.string.server_detail__delete_message.rememberString())
            } else {
                Text(Res.string.server_detail__delete_warn.rememberString())
            }
        }
    }
}

@Composable
fun ServerScope.LockDialog(state: DialogState, nnvl: ServerScope.Wrapper) {

    val confirmButton: @Composable () -> Unit = {
        if (nnvl.canBeLocked()) {
            TextButton({
                action(ServerVM.Action.Lock)
                state.hide()
            }) { Text(Res.string.dialog__action_ok.rememberString()) }
        }
    }
    Dialog(state) {
        DialogUI(
            modifier = Modifier.align(Alignment.Center),
            onDismissRequest = { state.hide() },
            title = { Text(Res.string.server_detail__lock_title.rememberString()) },
            confirmButton = confirmButton,
            dismissButton = { TextButton({ state.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            if (nnvl.canBeLocked()) {
                Text(Res.string.server_detail__lock_msg.rememberString())
            } else {
                Text(Res.string.server_detail__lock_warn.rememberString())
            }
        }
    }
}
